import { useTranslation } from 'react-i18next'
import { savingsVariant, type CardQuote, type QuotableOption } from '@/lib/hooks/useCardQuote'
import { cn } from '@/lib/utils'

interface SavingsTagProps {
  /** Percent saved against the no-contract monthly option (the API's `savingsPercentage`, API-189). */
  percent: number | null | undefined
  /** The option the saving is about; without one (a monthly contract), the tag is plain. */
  option?: QuotableOption | null
  /** That option's card quote (`useCardQuote`): it says whether the installments add interest. */
  quote?: CardQuote | null
  /** The up-front amount, formatted, when the tag should carry it (the pricing checkout, where the method is chosen). */
  upfrontAmount?: string
  className?: string
  'data-testid'?: string
}

/**
 * The green "Save X%" tag of an option. Which words it uses is decided by
 * `savingsVariant` (useCardQuote.ts), the one place that rule lives: plain when
 * the card saves as much, otherwise naming the methods that pay the up-front
 * price ("Save 23% with PIX or boleto"), and no tag at all when no method does.
 */
export function SavingsTag({ percent, option, quote, upfrontAmount, className, 'data-testid': testId }: SavingsTagProps) {
  const { t } = useTranslation(['checkout'])
  if (!percent) return null
  const variant = option ? savingsVariant(option, quote) : 'plain'
  if (!variant) return null
  const text =
    variant === 'plain'
      ? t('checkout:savings', { percent })
      : t(upfrontAmount ? `checkout:savingsOnAmount.${variant}` : `checkout:savingsOn.${variant}`, { percent, amount: upfrontAmount })
  return (
    <span className={cn('rounded bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700', className)} data-testid={testId}>
      {text}
    </span>
  )
}
